import {
    cadenceLabel, expandOccurrences, scheduleFrom, scheduleStartInstant,
} from '@/pages/game/live-events/model/schedule.js';
import { DEFAULTS_AHEAD, defaultsBlockers, publishesItself } from '@/pages/game/live-events/model/eventModel.js';
import { stableColor } from './flywheelStyle.js';

export const DAY = 86400000;
export const HORIZON_DAYS = 90;
export const PREVIEW_DATES = 5;
export const VIEW_BACK_DAYS = 120;
export const VIEW_AHEAD_DAYS = 366;
const ACTIVE_BACK_DAYS = 30;
const FIXED_STEP_MS = { MINUTE: 60000, HOUR: 3600000 };

// The org's operating zone, from the Upcoming feed: "player's local time" dates and every date shown here use it.
let operating = 'UTC';
export const setOperatingZone = (zone) => { operating = zone || 'UTC'; };
export const operatingZone = () => operating;

export const ITEM_KINDS = [
    { value: 'BEAT', label: 'Live Event', plural: 'Live Events' },
    { value: 'OBJECTIVE', label: 'Objective', plural: 'Objectives' },
    { value: 'MILESTONE', label: 'Milestone', plural: 'Milestones' },
    { value: 'BLOCK', label: 'Note block', plural: 'Note blocks' },
];

export const kindMeta = (kind) => ITEM_KINDS.find((k) => k.value === kind) || ITEM_KINDS[0];

const isBlank = (v) => v === null || v === undefined || v === ''
    || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

export const cleanOverride = (override) => {
    if (!override || typeof override !== 'object') return null;
    const out = Object.fromEntries(Object.entries(override).filter(([, v]) => !isBlank(v)));
    return Object.keys(out).length ? out : null;
};

export const scheduleOf = (event, item) => ({ ...scheduleFrom(event), ...(cleanOverride(item?.scheduleOverride) || {}) });

const canonical = (v) => {
    if (Array.isArray(v)) return v.length ? [...v].sort() : null;
    if (v && typeof v === 'object') {
        const out = Object.fromEntries(Object.keys(v).sort().map((k) => [k, canonical(v[k])]).filter(([, x]) => x != null));
        return Object.keys(out).length ? out : null;
    }
    return v === '' || v === undefined ? null : v;
};

// Blank fields, "never ends" and no zone (UTC) count as unset, so a value changed and changed back matches again.
export const sameSchedule = (a, b) => {
    const norm = (s) => canonical({
        ...s,
        timezone: s?.timezone || 'UTC',
        recurrence: s?.recurrence?.end?.type === 'NEVER' ? { ...s.recurrence, end: null } : s?.recurrence,
    });
    return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
};

export const lengthMs = (event, item) => (scheduleOf(event, item).durationSeconds || 0) * 1000;

const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
const addDays = (ymd, days) => new Date(Date.parse(`${ymd}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);

// Whole days from one local date ("YYYY-MM-DD…") to another.
export const daysBetween = (fromLocal, toLocal) => Math.round(
    (Date.parse(`${toLocal.slice(0, 10)}T00:00:00Z`) - Date.parse(`${fromLocal.slice(0, 10)}T00:00:00Z`)) / DAY);

// The schedule moved by whole days: its first run, its weekdays and an end date all shift, its time of day stays.
// Null when a rule pins the dates to the calendar (a monthly day or weekday), so they can't move with Starts.
export const shiftSchedule = (s, days) => {
    if (!s?.startLocal || (s.kind !== 'ONE_TIME' && s.intervalUnit === 'MONTH' && s.recurrence?.monthly)) return null;
    if (!days) return s;
    const rec = s.recurrence;
    const turn = (code) => WEEKDAY_CODES[(((WEEKDAY_CODES.indexOf(code) + days) % 7) + 7) % 7];
    return {
        ...s,
        startLocal: addDays(s.startLocal.slice(0, 10), days) + s.startLocal.slice(10),
        recurrence: rec ? {
            ...rec,
            ...(rec.byWeekday?.length ? { byWeekday: WEEKDAY_CODES.filter((c) => rec.byWeekday.map(turn).includes(c)) } : {}),
            ...(rec.end?.type === 'UNTIL' && rec.end.date ? { end: { ...rec.end, date: addDays(rec.end.date, days) } } : {}),
        } : rec,
    };
};

export const itemLengthMs = (item, event) => {
    if (item.kind === 'MILESTONE') return 0;
    if (item.kind === 'BEAT') return event ? lengthMs(event, item) : 0;
    return (item.durationSeconds || 0) * 1000;
};

export const startMsOf = (item) => Date.parse(item.startAt);

export const occurrencesBetween = (event, item, fromMs, toMs) => {
    if (!event || item.kind !== 'BEAT') return [];
    const s = scheduleOf(event, item);
    let anchor = scheduleStartInstant(s, operating);
    if (anchor == null || !Number.isFinite(anchor)) return [];
    const placed = startMsOf(item);
    const floor = Math.max(fromMs, Number.isFinite(placed) ? placed : fromMs);
    const until = item.horizonDays && Number.isFinite(placed) ? Math.min(toMs, placed + item.horizonDays * DAY) : toMs;
    if (until < floor) return [];
    if (s.kind === 'ONE_TIME') return anchor >= floor && anchor <= until ? [anchor] : [];
    const step = FIXED_STEP_MS[s.intervalUnit] ? FIXED_STEP_MS[s.intervalUnit] * Math.max(1, s.intervalValue || 1) : null;
    if (step && s.recurrence?.end?.type !== 'COUNT' && floor - anchor > step) anchor += Math.floor((floor - anchor) / step) * step;
    return expandOccurrences(s, anchor, operating, Math.ceil((until - anchor) / DAY) + 1).filter((ms) => ms >= floor && ms <= until);
};

// Far enough to reach any series that ends: expansion stops at its count or date, and at 500 dates.
const SERIES_DAYS = 10 * 366;

// True when the event's schedule (or the placement's override) ends after a count or on a date.
export const seriesEnds = (event, item) => {
    if (!event) return false;
    const end = scheduleOf(event, item).recurrence?.end?.type;
    return end === 'COUNT' || end === 'UNTIL';
};

// Every date from the placement's start: to the series' end when it has one, otherwise over the year ahead.
export const seriesOccurrences = (event, item, now = Date.now()) => {
    const start = startMsOf(item);
    return occurrencesBetween(event, item, start, seriesEnds(event, item) ? start + SERIES_DAYS * DAY : now + VIEW_AHEAD_DAYS * DAY);
};

export const viewOccurrences = (event, item, now = Date.now()) => (item.kind === 'BEAT'
    ? occurrencesBetween(event, item, now - VIEW_BACK_DAYS * DAY, now + VIEW_AHEAD_DAYS * DAY)
    : [startMsOf(item)]);

export const firstDates = (event, item, count = PREVIEW_DATES, now = Date.now()) => {
    const len = lengthMs(event, item);
    return occurrencesBetween(event, item, now - Math.max(len, 0), now + VIEW_AHEAD_DAYS * DAY)
        .filter((ms) => ms + len > now)
        .slice(0, count);
};

export const recurrenceLabel = (event, item) => {
    if (!event) return '';
    const s = scheduleOf(event, item);
    return s.kind === 'ONE_TIME' ? 'One-time' : cadenceLabel(s.intervalValue, s.intervalUnit);
};

export const isOverridden = (item) => item.kind === 'BEAT' && !!cleanOverride(item.scheduleOverride);

export const itemTitle = (item, eventsById, objectivesById) => {
    if (item.title) return item.title;
    if (item.kind === 'BEAT') return eventsById.get(item.beatId)?.name || 'Live Event';
    if (item.kind === 'OBJECTIVE') return objectivesById.get(item.objectiveId)?.name || 'Objective';
    return item.kind === 'MILESTONE' ? 'Milestone' : 'Note';
};

const KIND_COLOR = { OBJECTIVE: '--visual-color-6', MILESTONE: '--warning', BLOCK: '--visual-color-2' };

export const itemColorToken = (item) => item.color || (item.kind === 'BEAT' ? stableColor(item.beatId || item.id) : KIND_COLOR[item.kind]);

export const READY_BLOCKERS = {
    schedule: 'Set the schedule: when it starts, how often, and how long each run lasts.',
    config: 'Link the config it changes. Without a config, its dates have nothing to publish.',
};

export const readiness = (event) => {
    const blockers = [];
    if (!event?.startLocal) blockers.push(READY_BLOCKERS.schedule);
    if (event?.owner !== 'GAME_SERVER' && !(event?.configLinks || []).length) blockers.push(READY_BLOCKERS.config);
    return { ready: blockers.length === 0, blockers };
};

export const shipBlocker = (event, item) => {
    if (event.owner === 'GAME_SERVER') return null;
    if (!(event.configLinks || []).length) return 'No configs linked — nothing to ship.';
    if (!lengthMs(event, item)) return 'No length — a change would never revert.';
    return null;
};

// An event on DEFAULTS publishes its next dates itself once placed; null for one a person publishes. `stalled` is
// what stops it right now (the server's wording), in which case its dates wait for a person after all.
export const selfPublish = (event) => (publishesItself(event) ? { stalled: defaultsBlockers(event) } : null);

// True while the event's dates publish on their own: set to, and nothing stalling it.
export const publishesOnDefaults = (event) => {
    const self = selfPublish(event);
    return !!self && self.stalled.length === 0;
};

export const occKey = (itemId, ms) => `${itemId}:${ms}`;

export const indexInstances = (instances = []) => {
    const byKey = new Map();
    const shipped = new Set();
    instances.forEach((inst) => {
        byKey.set(occKey(inst.timelineItemId, Date.parse(inst.occStart)), inst);
        if (inst.publishedAt) shipped.add(inst.timelineItemId);
    });
    return { byKey, shipped };
};

export const DATE_STATUS = {
    SKIPPED: { label: 'Skipped', tone: 'neutral' },
    CANCELLED: { label: 'Cancelled', tone: 'neutral' },
    PUBLISHED: { label: 'Published', tone: 'info' },
    RUNNING: { label: 'Running', tone: 'success' },
    FINISHED: { label: 'Finished', tone: 'neutral' },
    WAITING: { label: 'Waiting', tone: 'warning' },
    MISSED: { label: 'Not published', tone: 'neutral' },
    STARTED: { label: 'Started', tone: 'neutral' },
    PAUSED: { label: 'Paused', tone: 'warning' },
    UPCOMING: { label: 'Not published yet', tone: 'neutral' },
};

// A started date is known only once its record is opened, which needs edit access; until then it shows as Started.
export const dateStatus = (inst, startMs, endMs, { paused = false, selfPublishing = false, now = Date.now() } = {}) => {
    if (!inst && now >= startMs) return { key: 'STARTED', detail: 'Its record isn’t loaded here, so whether it was published isn’t shown.' };
    if (inst?.skippedAt) return { key: 'SKIPPED', detail: inst.skipReason ? `Skipped: ${inst.skipReason}` : 'Nothing runs on it.' };
    if (inst?.cancelledAt) return { key: 'CANCELLED', detail: 'It was cancelled, so it doesn’t go out. A cancelled date can’t be published again.' };
    if (inst?.endedEarlyAt) return { key: 'FINISHED', detail: 'Ended early.' };
    if (inst?.publishedAt) {
        if (now < startMs) return { key: 'PUBLISHED', detail: 'It goes out to players when it starts.' };
        if (endMs == null || now < endMs) return { key: 'RUNNING', detail: 'Players are seeing it now.' };
        return { key: 'FINISHED', detail: 'Its window has closed.' };
    }
    if (paused && now < startMs) {
        return { key: 'PAUSED', detail: 'The event is paused on this flywheel, so this date doesn’t go out. Resume it to bring its dates back into Upcoming.' };
    }
    if (inst?.autoError) return { key: 'WAITING', detail: inst.autoError };
    if (now >= startMs) return { key: 'MISSED', detail: 'It started without being published.' };
    if (selfPublishing) {
        return {
            key: 'UPCOMING',
            detail: `It publishes itself on the event’s defaults once it is one of the next ${DEFAULTS_AHEAD} dates. If a Manual task or a rule holds it back, or it was taken back before, it waits in Upcoming for a person.`,
        };
    }
    return { key: 'UPCOMING', detail: 'It goes out once someone plans and publishes it in Upcoming.' };
};

export const relative = (fromMs, toMs) => {
    const mins = Math.round((toMs - fromMs) / 60000);
    if (mins < 60) return `${Math.max(1, mins)}m`;
    const hours = Math.round(mins / 60);
    if (hours < 48) return `${hours}h`;
    return `${Math.round(hours / 24)}d`;
};

const fmt = (ms, zone, opts) => {
    try {
        return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: zone }).format(new Date(ms));
    } catch {
        return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' }).format(new Date(ms));
    }
};

export const shortWhen = (ms, zone) => fmt(ms, zone || operating, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
});

export const longWhen = (ms, zone) => fmt(ms, zone || operating, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
});

export const dayWhen = (ms, zone) => fmt(ms, zone || operating, { weekday: 'short', month: 'short', day: 'numeric' });

export const zoneAbbr = (zone = operating, ms = Date.now()) => fmt(ms, zone, { timeZoneName: 'short' }).split(' ').pop();

export const zoneLabel = (zone = operating, ms = Date.now()) => {
    const abbr = zoneAbbr(zone, ms);
    return zone === 'UTC' || abbr === zone ? zone : `${zone} (${abbr})`;
};

export const instantToLocal = (ms, zone) => {
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
        }).formatToParts(new Date(ms));
        const get = (t) => parts.find((p) => p.type === t)?.value ?? '';
        return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    } catch {
        return '';
    }
};

export const objectiveSummary = (objective) => {
    if (!objective) return null;
    const parts = [objective.current && `${objective.current} now`, objective.benchmark && `target ${objective.benchmark}`].filter(Boolean);
    return parts.length ? parts.join(' · ') : null;
};

// The window a date serves in: its own once published, else its occurrence and the schedule's length.
export const dateWindow = (inst, event, item) => {
    const startMs = Date.parse(inst.window?.startsAt || inst.occStart);
    const len = event ? lengthMs(event, item) : 0;
    const endMs = inst.window?.endsAt ? Date.parse(inst.window.endsAt) : len ? Date.parse(inst.occStart) + len : null;
    return { startMs, endMs };
};

const isOut = (i) => i.publishedAt && !i.cancelledAt && !i.skippedAt && !i.endedEarlyAt;

// Published dates that haven't started: what Pause, Remove and Delete take back.
export const publishedAhead = (item, instances, now = Date.now()) => instances.filter((i) => i.timelineItemId === item.id
    && isOut(i) && Date.parse(i.window?.startsAt || i.occStart) > now);

// The dates each placed event is running now by its schedule, paused or not.
export const runningNow = (items, eventsById, now = Date.now()) => items
    .filter((item) => item.kind === 'BEAT' && eventsById.has(item.beatId))
    .flatMap((item) => {
        const event = eventsById.get(item.beatId);
        const len = lengthMs(event, item);
        if (!len) return [];
        return occurrencesBetween(event, item, now - len, now)
            .filter((ms) => ms <= now && now < ms + len)
            .map((startMs) => ({ itemId: item.id, startMs }));
    });

// What Pause, Remove and Delete would stop: published dates ahead, and the published dates running now.
export const datesOut = (items, eventsById, instances, now = Date.now()) => {
    const ahead = [];
    const running = [];
    items.filter((i) => i.kind === 'BEAT').forEach((item) => {
        ahead.push(...publishedAhead(item, instances, now));
        instances.filter((i) => i.timelineItemId === item.id && isOut(i)).forEach((inst) => {
            const { startMs, endMs } = dateWindow(inst, eventsById.get(item.beatId), item);
            if (startMs <= now && (endMs == null || now < endMs)) running.push({ inst, itemId: item.id, startMs, endMs });
        });
    });
    return { ahead, running };
};

const STATE_ORDER = {
    LIVE: 0, RUNNING: 1, WAITING: 1, UNPUBLISHED: 1, NEXT: 2, PAUSED: 3, IDLE: 4,
};

export const buildActivity = ({
    flywheels, items, events, instances = [], now = Date.now(),
}) => {
    const eventsById = new Map(events.map((e) => [e.id, e]));
    const fwById = new Map(flywheels.map((f) => [f.id, f]));
    const { byKey } = indexInstances(instances);
    const off = (itemId, ms) => {
        const inst = byKey.get(occKey(itemId, ms));
        return !!(inst?.skippedAt || inst?.endedEarlyAt || inst?.cancelledAt);
    };
    const rows = [];
    const cards = new Map(flywheels.map((f) => [f.id, { activeCount: 0, nextMs: null }]));
    const fromMs = now - ACTIVE_BACK_DAYS * DAY;
    const toMs = now + HORIZON_DAYS * DAY;

    items.forEach((item) => {
        const fw = fwById.get(item.flywheelId);
        const event = eventsById.get(item.beatId);
        if (item.kind !== 'BEAT' || !fw || !event) return;
        const card = cards.get(fw.id);
        if (!item.paused) card.activeCount += 1;
        const base = {
            itemId: item.id,
            flywheelId: fw.id,
            flywheelName: fw.name,
            beatId: event.id,
            title: item.title || event.name,
            category: event.category,
            cadence: recurrenceLabel(event, item),
            colorToken: itemColorToken(item),
        };
        if (item.paused) {
            rows.push({ ...base, state: 'PAUSED', detail: 'no new dates while paused', occMs: null });
            return;
        }
        const len = lengthMs(event, item);
        const occ = occurrencesBetween(event, item, fromMs, toMs).filter((ms) => !off(item.id, ms));
        const running = occ.find((ms) => ms <= now && len > 0 && now < ms + len);
        const next = occ.find((ms) => ms > now);
        if (next != null && (card.nextMs == null || next < card.nextMs)) card.nextMs = next;
        const runningInst = running != null ? byKey.get(occKey(item.id, running)) : null;
        const servedEnd = runningInst?.publishedAt ? dateWindow(runningInst, event, item).endMs : null;
        if (running != null && !runningInst) {
            rows.push({
                ...base, state: 'RUNNING', detail: `running now · ends in ${relative(now, running + len)}`, occMs: running,
            });
        } else if (running != null && runningInst.publishedAt && (servedEnd == null || now < servedEnd)) {
            rows.push({
                ...base, state: 'LIVE', detail: servedEnd ? `ends in ${relative(now, servedEnd)}` : 'no end set', occMs: running,
            });
        } else if (running != null && !runningInst?.publishedAt) {
            rows.push({
                ...base,
                state: runningInst?.autoError ? 'WAITING' : 'UNPUBLISHED',
                detail: runningInst?.autoError || `the date running now wasn’t published · ends in ${relative(now, running + len)}`,
                occMs: running,
            });
        } else if (next != null) {
            rows.push({ ...base, state: 'NEXT', detail: `${shortWhen(next)} · in ${relative(now, next)}`, occMs: next });
        } else {
            rows.push({ ...base, state: 'IDLE', detail: `no dates in the next ${HORIZON_DAYS} days`, occMs: null });
        }
    });

    rows.sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || a.title.localeCompare(b.title));
    return { rows, cards };
};

const addSide = (side, span) => {
    if (span.itemId && !side.itemIds.includes(span.itemId)) side.itemIds.push(span.itemId);
    if (span.flywheelName && !side.flywheelNames.includes(span.flywheelName)) side.flywheelNames.push(span.flywheelName);
};

export const buildClashes = ({
    items, events, flywheels, configFiles, tests = [], instances = [], now = Date.now(),
}) => {
    const eventsById = new Map(events.map((e) => [e.id, e]));
    const fwById = new Map(flywheels.map((f) => [f.id, f]));
    const fileName = new Map(configFiles.map((f) => [f.id, f.name]));
    const { byKey } = indexInstances(instances);
    const toMs = now + HORIZON_DAYS * DAY;
    const byFile = new Map();
    const add = (fileId, span) => byFile.set(fileId, [...(byFile.get(fileId) || []), span]);

    items.forEach((item) => {
        const event = eventsById.get(item.beatId);
        if (item.kind !== 'BEAT' || item.paused || !event) return;
        const files = [...new Set((event.configLinks || []).map((l) => l.configFileId))];
        if (!files.length) return;
        const len = Math.max(lengthMs(event, item), 60000);
        occurrencesBetween(event, item, now - len, toMs).forEach((start) => {
            const inst = byKey.get(occKey(item.id, start));
            if (start + len < now || inst?.skippedAt || inst?.cancelledAt || inst?.endedEarlyAt) return;
            const span = {
                key: `EVENT:${event.id}`, title: event.name, kind: 'EVENT', itemId: item.id,
                flywheelName: fwById.get(item.flywheelId)?.name, startMs: start, endMs: start + len,
            };
            files.forEach((fileId) => add(fileId, span));
        });
    });
    tests.forEach((t) => {
        const endMs = t.endMs ?? toMs;
        if (endMs < now || t.startMs > toMs) return;
        t.fileIds.forEach((fileId) => add(fileId, {
            key: `TEST:${t.id}`, title: t.name, kind: 'TEST', startMs: t.startMs, endMs,
        }));
    });

    const found = new Map();
    byFile.forEach((spans, fileId) => {
        spans.sort((x, y) => x.startMs - y.startMs);
        for (let i = 0; i < spans.length; i += 1) {
            for (let j = i + 1; j < spans.length && spans[j].startMs < spans[i].endMs; j += 1) {
                const [first, second] = spans[i].key < spans[j].key ? [spans[i], spans[j]] : [spans[j], spans[i]];
                if (first.key === second.key || (first.kind === 'TEST' && second.kind === 'TEST')) continue;
                const k = `${fileId}|${first.key}|${second.key}`;
                const hit = found.get(k) || {
                    fileId,
                    configName: fileName.get(fileId) || fileId,
                    a: { title: first.title, kind: first.kind, itemIds: [], flywheelNames: [] },
                    b: { title: second.title, kind: second.kind, itemIds: [], flywheelNames: [] },
                    startMs: Math.max(spans[i].startMs, spans[j].startMs),
                    times: 0,
                };
                addSide(hit.a, first);
                addSide(hit.b, second);
                hit.times += 1;
                found.set(k, hit);
            }
        }
    });
    return [...found.values()].sort((p, q) => p.startMs - q.startMs);
};

const TEST_PHASES = new Set(['SCHEDULED', 'LIVE', 'PAUSED']);
const msOrNull = (v) => (v ? Date.parse(v) : null);

// Live Experiments that are scheduled or running, with the config files they test.
export const testBands = (experiments = []) => experiments
    .filter((t) => TEST_PHASES.has(t.phase))
    .map((t) => ({
        id: t.id,
        name: t.name,
        phase: t.phase,
        startMs: msOrNull(t.startedAt || t.window?.startsAt),
        endMs: msOrNull(t.endedAt || t.window?.endsAt),
        fileIds: [...new Set((t.configLinks || []).map((l) => l.configFileId).filter(Boolean))],
        segments: t.audience?.segmentIds?.length || 0,
        exposurePct: t.audience?.exposurePct ?? 100,
    }))
    .filter((b) => Number.isFinite(b.startMs))
    .sort((a, b) => a.startMs - b.startMs);
